import React, { useState, useRef, useEffect } from 'react';
import './ContactSection.css';
import { FaLinkedin, FaEnvelope, FaPaperPlane } from 'react-icons/fa';
import profileImage from '../assets/images/LetsConnect.jpeg';
import { collection, addDoc, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebase-config';

function ContactSection() {
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [fields, setFields] = useState({ name: '', email: '', message: '' });
  const [honeypot, setHoneypot] = useState('');
  const [contactImage, setContactImage] = useState(profileImage);
  const loadTimeRef = useRef(Date.now());

  useEffect(() => {
    getDoc(doc(db, 'hero_config', 'main'))
      .then(snap => {
        if (snap.exists() && snap.data().contactImage) setContactImage(snap.data().contactImage);
      })
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    setFields(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Bot check 1: honeypot field filled
    if (honeypot) return;

    // Bot check 2: submitted too fast (< 3 seconds since page load)
    if (Date.now() - loadTimeRef.current < 3000) return;

    const { name, email, message } = fields;
    if (!name.trim() || !email.trim() || !message.trim()) return;

    setStatus('sending');
    try {
      await addDoc(collection(db, 'contactMessages'), {
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        read: false,
        createdAt: serverTimestamp(),
      });
      setStatus('success');
      setFields({ name: '', email: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="contact-frame">
      <section className="contact-section">
        <div className="contact-grid">
          {/* Left: Message & Form */}
          <div className="contact-message">
            <h2 className="contact-heading">Let's Connect</h2>
            <p className="contact-subtext">
              This space is more than a portfolio. It's a digital reflection of who I am — a legal
              professional, a writer, a thinker, and someone who believes in connection over credentials.
              Whether you're here to explore my journey, learn something new, share an idea, ask a
              question, or simply connect, you're welcome here. Explore. Read. Reach out.
            </p>
            <p className="contact-subtext">
              I read every message, and I'd love to know what brought you here. Sometimes, the most
              interesting things begin with a simple hello.
            </p>

            {status === 'success' ? (
              <div className="contact-success">
                <span className="contact-success-icon">✓</span>
                <h3>Message received!</h3>
                <p>Thank you for reaching out. I'll get back to you soon.</p>
                <button
                  className="send-btn"
                  style={{ marginTop: '1.2rem' }}
                  onClick={() => { setStatus('idle'); loadTimeRef.current = Date.now(); }}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit} autoComplete="off">
                {/* Honeypot — hidden from humans, bots fill it */}
                <input
                  type="text"
                  name="website"
                  value={honeypot}
                  onChange={e => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  aria-hidden="true"
                  style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                  autoComplete="off"
                />

                <div className="form-group">
                  <input
                    type="text"
                    name="name"
                    id="contact-name"
                    required
                    value={fields.name}
                    onChange={handleChange}
                  />
                  <label htmlFor="contact-name">Your Name</label>
                </div>
                <div className="form-group">
                  <input
                    type="email"
                    name="email"
                    id="contact-email"
                    required
                    value={fields.email}
                    onChange={handleChange}
                  />
                  <label htmlFor="contact-email">Your Email</label>
                </div>
                <div className="form-group">
                  <textarea
                    name="message"
                    id="contact-message"
                    rows="5"
                    required
                    value={fields.message}
                    onChange={handleChange}
                  />
                  <label htmlFor="contact-message">Your Message</label>
                </div>

                {status === 'error' && (
                  <p style={{ color: '#c25b2a', fontSize: '0.9rem', marginBottom: '0.8rem' }}>
                    Something went wrong. Please try again.
                  </p>
                )}

                <button type="submit" className="send-btn" disabled={status === 'sending'}>
                  <FaPaperPlane className="send-icon" />
                  {status === 'sending' ? 'Sending…' : 'Send Message'}
                </button>
              </form>
            )}
          </div>

          {/* Right: Sticky Image and Icons */}
          <div className="contact-sticky-block">
            <div className="contact-image-block">
              <div className="contact-image-deco">
                <img src={contactImage} alt="Pooja Biswal" className="contact-image" />
                <span className="contact-blob" aria-hidden="true"></span>
              </div>
              <div className="contact-icons">
                <a
                  href="mailto:prachi.biswal1@gmail.com"
                  aria-label="Email"
                  className="icon-circle"
                >
                  <FaEnvelope />
                </a>
                <a
                  href="https://www.linkedin.com/in/pooja-biswal-5ab931112/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  className="icon-circle"
                >
                  <FaLinkedin />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ContactSection;
